<script setup>
import { computed, ref, onMounted } from 'vue';
import { getPartnerMeta, SMARTWEL_SUBSCRIPTION_PRODUCT_ID } from '@/constants/partner';
import { getProduct } from '@/api/PaymentApi';
import manseLogoUrl from '@manseImages/logo_b.png';
import SmartwelResponsiveSheet from './SmartwelResponsiveSheet.vue';

/**
 * 비구독 사용자에게 노출되는 정기구독 권유 시트 (구독 1단계).
 * - 모바일: 바텀시트 / PC: 팝업 (SmartwelResponsiveSheet가 분기)
 * - 헤더(제목 + 1/2 스텝 + 닫기) / Hero(파트너×낭만만세력 로고 락업) / 가격카드 / 8종 혜택 그리드 / CTA
 * - 가격(정가/할인가/할인율)은 상품(productId)의 실제 데이터로 동적 표시. 할인 없으면 뱃지·정가 숨김.
 */
const PRODUCT_ID = SMARTWEL_SUBSCRIPTION_PRODUCT_ID;
const emit = defineEmits(['close', 'subscribe']);

// 헤더(Header.vue)와 동일하게 myInfo.partner.provider 기준 파트너(복지찬스) 워드마크 사용
const { partner } = useMember();
const partnerMeta = computed(() => getPartnerMeta(partner.value?.provider));
const partnerLogoUrl = computed(() => partnerMeta.value?.logoUrl ?? null);
const partnerName = computed(() => partnerMeta.value?.name ?? '복지찬스');

// 상품 가격 (정가/할인가/할인율) — 상품 데이터 기반 동적 표시
const product = ref(null);
onMounted(async () => {
  try {
    product.value = await getProduct(PRODUCT_ID);
  } catch (e) {
    product.value = null;
  }
});
// 가격 — 상품(getProduct) 응답에서 동적 바인딩. 단위는 원 (복지P 1:1).
//  · 정가 = basePrice, 할인가 = salePrice(없으면 price). basePrice > salePrice일 때만 정가/할인 표시.
const basePrice = computed(() => Number(product.value?.basePrice ?? 0));
const salePrice = computed(() => Number(product.value?.salePrice ?? product.value?.price ?? 0));
const hasDiscount = computed(() => basePrice.value > salePrice.value);
const basePriceText = computed(() => basePrice.value.toLocaleString());
const salePriceText = computed(() => salePrice.value.toLocaleString());
// 할인 배지 — discountLabel 우선, 없으면 basePrice/salePrice로 할인율 계산
const discountBadge = computed(() => {
  if (!hasDiscount.value) return '';
  if (product.value?.discountLabel) return String(product.value.discountLabel);
  const pct = Math.round((1 - salePrice.value / basePrice.value) * 100);
  return `복지찬스 회원 전용 ${pct}%↓`;
});

// 구독 시 무제한 이용 가능한 8종 서비스 (시안 A 기준)
const SERVICES = [
  { name: '낭만일진', badge: 'HOT', variant: 'hot' },
  { name: '낭만택일' },
  { name: '월운세' },
  { name: '신년운세' },
  { name: '용신 전문가' },
  { name: '용신 라이트', badge: 'HOT', variant: 'hot' },
  { name: '성향분석', badge: '추천', variant: 'pick' },
  { name: '낭만궁합' },
];
</script>

<template>
  <SmartwelResponsiveSheet @close="emit('close')">
    <div class="sub-modal">
      <!-- 헤더: 제목 + 스텝(1/2) + 닫기 -->
      <div class="sub-header">
        <h2 class="sub-header__title">낭만만세력 구독</h2>
        <div class="sub-step">
          <span class="sub-step__dot active" />
          <span class="sub-step__dot" />
          <span class="sub-step__count">1/2</span>
        </div>
        <button type="button" class="sub-close" aria-label="닫기" @click="emit('close')"></button>
      </div>

      <!-- 본문 -->
      <div class="sub-body">
        <!-- 메인 카피 -->
        <div class="sub-hero">
          <div class="sub-hero__lockup">
            <img
              v-if="partnerLogoUrl"
              :src="partnerLogoUrl"
              :alt="partnerName"
              class="sub-hero__logo sub-hero__logo--partner"
            />
            <span v-if="partnerLogoUrl" class="sub-hero__x" aria-hidden="true">×</span>
            <img
              :src="manseLogoUrl"
              alt="낭만만세력"
              class="sub-hero__logo sub-hero__logo--manse"
            />
          </div>
          <div class="sub-hero__title">
            궁합, 오늘 운세,<br />
            사주 <span class="hl">MBTI</span>까지~
          </div>
        </div>

        <!-- 가격 카드 (상품 데이터 동적) -->
        <div class="sub-price">
          <div class="sub-price__left">
            <span v-if="discountBadge" class="sub-price__badge">{{ discountBadge }}</span>
            <div v-if="hasDiscount" class="sub-price__original">정가 {{ basePriceText }}원</div>
          </div>
          <div class="sub-price__right">
            <div class="sub-price__label">복지포인트로 부담없이</div>
            <div class="sub-price__now">
              <span class="sub-price__num">{{ salePriceText }}</span>
              <span class="sub-price__unit">원</span>
            </div>
          </div>
        </div>

        <!-- 구독 혜택 8종 -->
        <div class="sub-section-label">구독하면 무제한 이용 가능해요</div>
        <div class="sub-grid">
          <div
            v-for="svc in SERVICES"
            :key="svc.name"
            class="sub-item"
            :class="svc.variant ? `sub-item--${svc.variant}` : ''"
          >
            <span class="sub-item__check">✓</span>{{ svc.name }}
            <span
              v-if="svc.badge"
              class="sub-item__badge"
              :class="`sub-item__badge--${svc.variant}`"
            >
              {{ svc.badge }}
            </span>
          </div>
        </div>
        <div class="sub-note">오늘의 운세부터 월 운세까지 매일 새로워요</div>
      </div>

      <!-- 푸터 -->
      <div class="sub-footer">
        <button type="button" class="sub-next-btn" @click="emit('subscribe')">
          구독 신청하기
          <span class="sub-next-btn__arrow">→</span>
        </button>
      </div>
    </div>
  </SmartwelResponsiveSheet>
</template>

<style lang="scss" scoped>
.sub-modal {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Pretendard Variable', sans-serif;
  letter-spacing: -0.3px;
  word-break: keep-all;
  color: #1f2937;
}

/* 헤더 */
.sub-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}

/* 모바일 바텀시트: 헤더 좌우 16px */
.srs-sheet--mobile .sub-header {
  padding: 16px;
}

.sub-header__title {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 800;
  color: #1f2937;
}

.sub-step {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  margin-left: auto;
  margin-right: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
}

.sub-step__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d1d5db;

  &.active {
    background: #1e3a5f;
  }
}

.sub-step__count {
  margin-left: 2px;
}

.sub-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: url('@manseImages/icon_close_1.svg') no-repeat center / contain;
  cursor: pointer;
}

/* 본문 */
.sub-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 22px 12px;
}

/* Hero */
.sub-hero {
  text-align: center;
  margin-bottom: 14px;
}

.sub-hero__lockup {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  /* 원래 태그 칩에 깔려있던 옅은 배경 유지 */
  padding: 7px 14px;
  background: #eef2f7;
  border-radius: 8px;
}

.sub-hero__logo {
  width: auto;
  display: block;
}

.sub-hero__logo--partner {
  height: 16px;
}

/* 낭만만세력 로고(logo_b.png)는 여백이 있어 같은 높이면 텍스트가 작아 보임.
   헤더(만세력 40px : 파트너 24px ≈ 1.67배) 비율에 맞춰 키움 */
.sub-hero__logo--manse {
  height: 24px;
  margin-top: -4px;
}

.sub-hero__x {
  font-size: 13px;
  font-weight: 400;
  color: #9ca3af;
  line-height: 1;
}

.sub-hero__title {
  font-size: 22px;
  font-weight: 800;
  color: #1f2937;
  letter-spacing: -0.8px;
  line-height: 1.3;

  .hl {
    color: #f4823c;
  }
}

/* 가격 카드 */
.sub-price {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 20px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2c4a75 100%);
  color: #fff;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(244, 130, 60, 0.2) 0%, transparent 65%);
    border-radius: 50%;
    pointer-events: none;
  }
}

.sub-price__left,
.sub-price__right {
  position: relative;
  z-index: 1;
}

.sub-price__right {
  flex-shrink: 0;
  text-align: right;
}

/* 좁은 폭에서 좌측이 줄어들도록(가격은 고정), 배지는 줄바꿈 허용 */
.sub-price__left {
  min-width: 0;
}

.sub-price__badge {
  display: inline-block;
  background: #f4823c;
  color: #fff;
  padding: 3px 9px;
  border-radius: 5px;
  font-size: 12.5px;
  font-weight: 800;
  margin-bottom: 6px;
}

.sub-price__original {
  font-size: 18px;
  font-weight: 700;
  opacity: 0.75;
  text-decoration: line-through;
  text-decoration-thickness: 2.5px;
}

.sub-price__label {
  font-size: 11.5px;
  font-weight: 500;
  margin-bottom: 2px;
}

.sub-price__now {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 3px;
}

.sub-price__num {
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -1.5px;
  line-height: 1;
}

.sub-price__unit {
  font-size: 14px;
  font-weight: 700;
  opacity: 0.9;
}

/* 섹션 라벨 */
.sub-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;

  &::before {
    content: '';
    width: 3px;
    height: 14px;
    background: #f4823c;
    border-radius: 2px;
  }
}

/* 8종 서비스 그리드 (2x4) */
.sub-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 10px;
}

.sub-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 12px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  transition: background 0.15s;

  &:hover {
    background: #eef2f7;
  }

  &--hot,
  &--pick {
    background: #fff7ed;

    &:hover {
      background: #ffedd5;
    }
  }
}

.sub-item__check {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1e3a5f;
  border-radius: 50%;
  color: #fff;
  font-size: 10px;
  font-weight: 900;

  .sub-item--hot &,
  .sub-item--pick & {
    background: #f4823c;
  }
}

.sub-item__badge {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  padding: 3px 7px;
  border-radius: 4px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.3px;
  line-height: 1.2;
  color: #fff;

  &--hot {
    background: #f4823c;
  }

  &--pick {
    background: #7c3aed;
  }
}

.sub-note {
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #eef2f7;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #1e3a5f;
  text-align: center;
}

/* 푸터 */
.sub-footer {
  flex-shrink: 0;
  padding: 12px 22px 18px;
  border-top: 1px solid #f3f4f6;
  background: #fff;
}

.sub-next-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #1e3a5f;
  color: #fff;
  font-size: 15.5px;
  font-weight: 800;
  font-family: inherit;
  letter-spacing: -0.3px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(30, 58, 95, 0.2);
  transition: all 0.15s;

  &:hover {
    background: #15294a;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(30, 58, 95, 0.3);
  }

  &__arrow {
    font-size: 14px;
    transition: transform 0.15s;
  }

  &:hover &__arrow {
    transform: translateX(2px);
  }
}

/* PC 팝업: body 좌우 32px, footer 좌우·하단 32px (헤더 제외) */
.srs-sheet--pc .sub-body {
  padding-left: 32px;
  padding-right: 32px;
}
.srs-sheet--pc .sub-footer {
  padding-left: 32px;
  padding-right: 32px;
  padding-bottom: 32px;
}
</style>
